import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { CapacitorCalendar } from "@ebarooni/capacitor-calendar";
import axiosLogin from "./axiosLogin";

/**
 * Handlers de las acciones de push (botones de la notificacion) — separados
 * de PushBootstrap para no acoplar el componente con logica de negocio.
 *
 * Contrato de datos: el back (FirebasePushSender) manda en `data` los campos:
 *   - url        (string) — ruta interna para el tap del banner
 *   - categoria  (string) — id de UNNotificationCategory / accion, ej "recordatorio-turno"
 *   - idTurno    (string, opcional) — solo en tipos que lo tienen (recordatorio)
 *   - idProtocolo (string, opcional)
 *   - codigoInforme / tipoInforme / estudioInforme (opcionales)
 *
 * Todos son STRINGS aunque el valor conceptual sea numerico — FCM data es
 * un dict<string,string> a nivel plataforma, no se puede meter numeros.
 */

const PROX_TURNOS_ROUTE = "/proximosTurnos";

/**
 * Formato aceptado: "-34.638871, -58.561250" (lat, lng) — puede o no tener
 * espacio despues de la coma. Devuelve null si el string no matchea.
 */
const parsearCoordenadaGps = (coord) => {
  if (typeof coord !== "string") return null;
  const [rawLat, rawLng] = coord.split(",").map((s) => s?.trim());
  const lat = Number(rawLat);
  const lng = Number(rawLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

/**
 * Trae los turnos propios del paciente logueado y busca el turno indicado.
 * No incluye familiares — si el turno es de un familiar sin cuenta propia,
 * fallback a la pagina de proximos turnos.
 */
const buscarTurnoPorId = async (idTurno) => {
  const numId = Number(idTurno);
  if (!Number.isFinite(numId)) return null;

  try {
    const client = await axiosLogin();
    const res = await client.get("/his/turnos/getxPaciente/");
    const turnos = Array.isArray(res?.data) ? res.data : [];
    return turnos.find((t) => Number(t?.idturno) === numId) || null;
  } catch (err) {
    console.warn("[pushActions] getxPaciente fallo:", err?.message || err);
    return null;
  }
};

/**
 * Accion "Como llegar": abre Google Maps con navegacion hacia el centro
 * del turno. Si no puede resolver la coordenada, navega a proximosTurnos
 * como fallback (nunca deja al user sin feedback).
 *
 * @param {{ data: object, history: import("history").History }} args
 */
export const handleComoLlegar = async ({ data, history }) => {
  const turno = await buscarTurnoPorId(data?.idTurno);
  const coord = parsearCoordenadaGps(turno?.coordenadaGps);

  if (!coord) {
    // Fallback: mostrar la pagina de turnos, el user puede tocar el mapa desde ahi.
    if (history) history.push(PROX_TURNOS_ROUTE);
    return;
  }

  const url =
    `https://www.google.com/maps/dir/?api=1` +
    `&destination=${coord.lat},${coord.lng}&dir_action=navigate`;

  try {
    await Browser.open({ url });
  } catch (err) {
    console.warn("[pushActions] Browser.open fallo:", err?.message || err);
  }
};

/**
 * Accion "Ver preparacion": navega a proximosTurnos pasando en el state
 * el idTurno para que la pagina auto-abra el ModalPreparaciones al montar.
 */
export const handleVerPreparacion = ({ data, history }) => {
  if (!history) return;
  const idTurno = Number(data?.idTurno);
  history.push(PROX_TURNOS_ROUTE, {
    abrirPreparacionIdTurno: Number.isFinite(idTurno) ? idTurno : null,
  });
};

/**
 * Accion "Subi tu orden": navega a proximosTurnos con el idTurno en el state
 * para que la pagina auto-abra el ModalSubirOrden de ese turno.
 */
export const handleSubirOrden = ({ data, history }) => {
  if (!history) return;
  const idTurno = Number(data?.idTurno);
  history.push(PROX_TURNOS_ROUTE, {
    abrirSubirOrdenIdTurno: Number.isFinite(idTurno) ? idTurno : null,
  });
};

/**
 * Accion "Gestionar turno": segun negocio, "tiene todo lo que puede hacer con
 * su turno" — alcanza con llevarlo a proximosTurnos, donde estan todas las
 * acciones (cancelar, subir orden, ver preparacion, abonar, etc.).
 */
export const handleGestionarTurno = ({ history }) => {
  if (history) history.push(PROX_TURNOS_ROUTE);
};

/**
 * True si el push es uno de confirmacion de turno (categorias confirmar-turno y
 * confirmar-turno-bg, tipos 24/27 del back) y trae el idTurno.
 */
const esPushDeConfirmacion = (data) =>
  typeof data?.categoria === "string" &&
  data.categoria.startsWith("confirmar-turno") &&
  Number.isFinite(Number(data?.idTurno));

/**
 * El tap sobre el banner de un push de confirmacion se trata como el boton
 * "Confirmá tu turno". Motivo: en iOS los botones solo aparecen con long-press;
 * el paciente que tapea directo tiene que llegar igual al modal de confirmacion.
 * Cualquier otro tap sigue siendo "tap" (navega a data.url).
 */
export const resolverActionId = (actionId, data) =>
  actionId === "tap" && esPushDeConfirmacion(data) ? "confirmar-turno" : actionId;

/**
 * Accion "Confirma tu turno": abre proximosTurnos con el idTurno en el state;
 * la pagina abre el modal "¿Estas seguro que deseas confirmar el turno?" y el
 * modal hace el POST. (Con confirmToken el boton confirma en background sin
 * pasar por aca; este es el camino cuando la app se abre.)
 */
export const handleConfirmarTurno = ({ data, history }) => {
  if (!history) return;
  const idTurno = Number(data?.idTurno);
  history.push(PROX_TURNOS_ROUTE, {
    confirmarIdTurno: Number.isFinite(idTurno) ? idTurno : null,
  });
};

/**
 * Accion "Cambia tu turno": las opciones de cambiar/cancelar viven en la card
 * del turno en proximosTurnos. Alcanza con llevarlo ahi.
 */
export const handleCambiarTurno = ({ history }) => {
  if (history) history.push(PROX_TURNOS_ROUTE);
};

// Duracion del evento en el calendario (la practica no informa duracion real).
const DURACION_EVENTO_MIN = 30;
// Alarmas del calendario, en minutos antes. 120 = misma anticipacion que el push T-2h.
const ALERTAS_MINUTOS_ANTES = [120];

/**
 * turno.fecha viene como ISO y turno.hora como "HH:MM". Mismo criterio que usa
 * ProximosTurnos.verificarHorarios (new Date(fecha) + setHours) para que el
 * evento caiga en el mismo instante que la app considera "el turno".
 */
const componerInicioTurno = (turno) => {
  const inicio = new Date(turno?.fecha);
  if (Number.isNaN(inicio.getTime())) return null;
  const [hh, mm] = String(turno?.hora || "").split(":").map(Number);
  inicio.setHours(Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0);
  return inicio;
};

/**
 * Accion "Agenda tu turno": abre el editor de eventos NATIVO del telefono
 * (Google Calendar en Android, Calendario en iOS) prellenado con el turno.
 * El paciente solo toca Guardar, y el sistema le manda su propio recordatorio.
 *
 * Este flujo con prompt no pide permiso de calendario en ninguna plataforma.
 * eventIdOptional=true es clave en Android: sin el, el plugin lee el calendario
 * para detectar el id del evento nuevo y eso SI requiere READ_CALENDAR.
 *
 * Fallback (web, turno no encontrado, error del plugin): abre proximos turnos.
 */
export const handleAgendar = async ({ data, history }) => {
  const fallback = () => {
    if (history) history.push(PROX_TURNOS_ROUTE);
  };
  if (!Capacitor.isNativePlatform()) {
    fallback();
    return;
  }

  const turno = await buscarTurnoPorId(data?.idTurno);
  const inicio = turno ? componerInicioTurno(turno) : null;
  if (!inicio) {
    fallback();
    return;
  }

  const fin = new Date(inicio.getTime() + DURACION_EVENTO_MIN * 60 * 1000);
  const titulo = `Turno ${turno.especialidad || ""} - DIM`.replace(/\s+/g, " ").trim();
  const lugar = [turno.centro, turno.ubicacion].filter(Boolean).join(" - ");
  const notas = [
    turno.medico ? `Profesional: ${turno.medico}` : null,
    "Recordá revisar la preparación y traer tu orden. Todo en la app DIM.",
  ]
    .filter(Boolean)
    .join("\n");

  try {
    await CapacitorCalendar.createEventWithPrompt({
      title: titulo,
      location: lugar || undefined,
      startDate: inicio.getTime(),
      endDate: fin.getTime(),
      isAllDay: false,
      alertOffsetInMinutes: ALERTAS_MINUTOS_ANTES,
      notes: notas,
      eventIdOptional: true,
    });
  } catch (err) {
    console.warn("[pushActions] createEventWithPrompt fallo:", err?.message || err);
    fallback();
  }
};

/**
 * Accion "Mostra tu QR" — PLACEHOLDER.
 * TODO(negocio): no esta definido que QR muestra ni donde vive en la app (hoy no
 * hay componente QR del paciente fuera de la terminal). Mientras tanto abre
 * proximos turnos para que el boton no sea un tap muerto.
 */
export const handleMostrarQr = ({ history }) => {
  if (history) history.push(PROX_TURNOS_ROUTE);
};

/**
 * Handler para el tap del banner completo (actionId="tap"): navega a la ruta
 * interna que el back mando en data.url. Rechaza absolutas y protocol-relative
 * — mismo criterio que el listener notificationActionPerformed en PushBootstrap.
 */
const handleTapBanner = ({ data, history }) => {
  if (esPushDeConfirmacion(data)) {
    handleConfirmarTurno({ data, history });
    return;
  }
  const url = data?.url;
  const valida = typeof url === "string" && url.startsWith("/") && !url.startsWith("//");
  if (valida && history) history.push(url);
};

/**
 * Router de acciones — decide que handler correr segun actionId.
 * Devuelve true si consumio la accion, false si el caller debe seguir
 * con su logica default.
 */
export const runPushAction = async ({ actionId, data, history }) => {
  switch (actionId) {
    case "como-llegar":
      await handleComoLlegar({ data, history });
      return true;
    case "ver-preparacion":
      handleVerPreparacion({ data, history });
      return true;
    case "subir-orden":
      handleSubirOrden({ data, history });
      return true;
    case "gestionar-turno":
      handleGestionarTurno({ history });
      return true;
    case "confirmar-turno":
      handleConfirmarTurno({ data, history });
      return true;
    case "cambiar-turno":
      handleCambiarTurno({ history });
      return true;
    case "agendar":
      await handleAgendar({ data, history });
      return true;
    case "mostrar-qr":
      handleMostrarQr({ history });
      return true;
    case "tap":
      handleTapBanner({ data, history });
      return true;
    default:
      return false;
  }
};
